import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Mail, Users } from "lucide-react";
import { useTran, useFormDirty } from "@/hooks";
import AppDialog from "@/components/common/AppDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ShareSurveyGuestTab from "./ShareSurveyGuestTab";
import ShareSurveyInternalTab from "./ShareSurveyInternalTab";
import { useShareSurveyStore } from "../hooks/useShareSurveyStore";

const ShareSurveyDialog = () => {
  const { t } = useTran();
  const shareStore = useShareSurveyStore();
  const { open, survey } = shareStore;

  const formValues = useMemo(
    () => ({
      guestEmail: shareStore.guestEmail,
      guestMessage: shareStore.guestMessage,
      selectedInvitesCount: shareStore.selectedInvitesCount,
    }),
    [
      shareStore.guestEmail,
      shareStore.guestMessage,
      shareStore.selectedInvitesCount,
    ],
  );
  const isDirty = useFormDirty(formValues, open);

  const handleSendInternal = async () => {
    await shareStore.sendInternalInvites();
  };

  const handleSendGuest = async () => {
    await shareStore.sendGuestInvite();
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) shareStore.closeDialog();
      }}
      isDirty={isDirty}
      title={survey?.title || t("Share survey")}
      description={t(
        "Invite internal users or a guest email. Only invited recipients can respond.",
      )}
      size="lg"
      bodyClassName="space-y-4"
    >
      <Tabs
        value={shareStore.mode}
        onValueChange={shareStore.setMode}
        className="flex flex-col h-[380px]"
      >
        <TabsList>
          <TabsTrigger value="internal" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> {t("Internal users")}
          </TabsTrigger>
          <TabsTrigger value="guest" className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> {t("Guest email")}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="internal"
          className="flex flex-1 flex-col  overflow-hidden"
        >
          <ShareSurveyInternalTab onSendInvites={handleSendInternal} />
        </TabsContent>

        <TabsContent
          value="guest"
          className="flex flex-1 flex-col overflow-hidden"
        >
          <ShareSurveyGuestTab onSendInvite={handleSendGuest} />
        </TabsContent>
      </Tabs>
    </AppDialog>
  );
};

export default observer(ShareSurveyDialog);
